// Helpers to create a PESO employer's first vacancy + apply employer branding.
// Shared by the employer wizard (immediate-session path) and the deferred
// dashboard post (when email confirmation defers login).
import api, { UPLOAD_TIMEOUT } from '../../lib/api';
import { NATURE_TO_JOB } from './pesoFields';

const PENDING_KEY = 'peso_pending_vacancy';

// Session-level guard so a re-render / StrictMode double-mount can't post twice
// while one attempt is in flight. The stash itself is only cleared on success.
let _postInFlight = false;
export function claimPendingPost() {
  if (_postInFlight) return false;
  _postInFlight = true;
  return true;
}
export function releasePendingPost() { _postInFlight = false; }

export function stashPendingVacancy(payload) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(payload)); } catch {}
}
export function readPendingVacancy() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function clearPendingVacancy() {
  try { localStorage.removeItem(PENDING_KEY); } catch {}
}

export async function uploadImageAuthed(file, token) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/api/upload-image', formData, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: UPLOAD_TIMEOUT,
  });
  if (!res.data?.success) throw new Error('image upload failed');
  return res.data.image_url;
}

// Resolve a Bohol Trabaho category id by name. The PESO->BT mapping is resolved at
// submit time from the office config and carried on the vacancy as `bt_category`,
// so this stays office-agnostic and works on the deferred dashboard-post path too.
async function resolveCategoryId(btName) {
  try {
    const target = (btName || 'Other').toLowerCase();
    const res = await api.get('/api/categories');
    const cats = res.data?.categories || res.data || [];
    const match = cats.find((c) => (c.name || '').toLowerCase() === target);
    return match?.id || null;
  } catch {
    return null;
  }
}

// Compose the seeker-facing "Qualifications" block from the structured NSRP Form 2 Section IV
// fields. Returns '' when nothing is set. This replaces the old single free-text qualifications
// textarea; the full structured version still lives in profile.nsrp_data.requirements.
export function buildQualificationsText(req) {
  if (!req) return '';
  const lines = [];
  if (req.work_experience_months) lines.push(`Work Experience: ${req.work_experience_months} month(s)`);
  if (req.educational_level) lines.push(`Education: ${req.educational_level}`);
  if (req.course_major) lines.push(`Course/Major: ${req.course_major}`);
  if (req.license) lines.push(`License: ${req.license}`);
  if (req.eligibility) lines.push(`Eligibility: ${req.eligibility}`);
  if (req.certification) lines.push(`Certification: ${req.certification}`);
  if (req.languages_spoken) lines.push(`Language/dialect: ${req.languages_spoken}`);
  if (req.sex && req.sex !== 'No Preference') lines.push(`Preferred Sex: ${req.sex}`);
  if (req.civil_status && req.civil_status !== 'No Preference') lines.push(`Preferred Civil Status: ${req.civil_status}`);
  if (req.other_qualifications) lines.push(req.other_qualifications.trim());
  return lines.join('\n');
}

// Compose the long-form job description from the PESO vacancy fields.
export function buildVacancyDescription(v) {
  const parts = [];
  if (v.description) parts.push(v.description.trim());
  // Structured Section IV (new) with a legacy fallback for any pre-existing stashed vacancy
  // that still carries the old free-text `qualifications` string.
  const quals = buildQualificationsText(v.requirements) || (v.qualifications ? v.qualifications.trim() : '');
  if (quals) parts.push(`Qualifications:\n${quals}`);
  if (v.nature_of_work) parts.push(`Nature of Work: ${v.nature_of_work}`);           // verbatim Form 2 label
  else if (v.employment_type_label) parts.push(`Employment Type: ${v.employment_type_label}`); // legacy stash
  if (v.salary_note) parts.push(`Salary & Benefits: ${v.salary_note.trim()}`);
  if (v.how_to_apply) parts.push(`How to Apply:\n${v.how_to_apply.trim()}`);
  if (v.panglao_category) parts.push(`PESO Category: ${v.panglao_category}`);
  return parts.join('\n\n');
}

/**
 * Create the vacancy (POST /api/jobs). `vacancy` is the stashed/built object.
 */
export async function postPesoVacancy(vacancy, token) {
  const category_id = await resolveCategoryId(vacancy.bt_category);
  // Null-category guard: a PESO draft with no category_id would be published (PESO approve does no
  // validation) miscategorized/unsearchable. Throw so the caller stashes + retries instead.
  if (!category_id) throw new Error('Could not resolve a job category for this vacancy.');
  // Nature of Work -> typed job columns (slug drives the map; legacy stash uses work_schedule_value).
  const mapped = NATURE_TO_JOB[vacancy.nature_value] || null;
  const body = {
    title: vacancy.title,
    description: buildVacancyDescription(vacancy),
    category_id,
    locations: vacancy.location ? [vacancy.location] : [],
    location: vacancy.location || '',
    positions_available: Math.max(1, parseInt(vacancy.positions_available, 10) || 1),
    salary_min: vacancy.salary_min ? parseInt(vacancy.salary_min, 10) : 0,
    salary_max: vacancy.salary_max ? parseInt(vacancy.salary_max, 10) : 0,
    salary_period: vacancy.salary_period || 'monthly',
    work_schedule: mapped ? mapped.work_schedule : (vacancy.work_schedule_value || 'full_time'),
    type: mapped ? mapped.type : 'permanent',
    benefits: Array.isArray(vacancy.benefits) ? vacancy.benefits : [],
    image_url: vacancy.image_url || null,
    // Saved as a DRAFT pending PESO-staff approval; the approve endpoint publishes it.
    status: 'draft',
  };
  if (vacancy.expires_at) body.expires_at = vacancy.expires_at;
  return api.post('/api/jobs', body, { headers: { Authorization: `Bearer ${token}` } });
}

// Apply employer branding (logo -> avatar_url, fb link, description).
export async function applyEmployerMeta(employerId, meta, token) {
  const updates = {};
  if (meta.avatar_url) updates.avatar_url = meta.avatar_url;
  if (meta.facebook) updates.social_media_links = { facebook: meta.facebook };
  if (meta.business_description) updates.business_description = meta.business_description;
  if (Object.keys(updates).length === 0) return null;
  return api.put(`/api/employers/${employerId}`, updates, { headers: { Authorization: `Bearer ${token}` } });
}
